import { Table } from 'antd';
import { type ColumnFilterItem, type FilterDropdownProps } from 'antd/es/table/interface';
import { pick } from 'lodash/fp';
import { type Key, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type WebPageListDataFragment } from '../../../api/fragments/WebPageListData';
import { useListWebPagesQuery } from '../../../api/queries/listWebPages';
import { CompanyFilterListCollection, WebPageSortingField } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import { type PaginatedTableProps } from '../../../components/PaginatedTable';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import { getDateTimeOffsetFormat } from '../../../utilities/date';
import makeGetSortingRule from '../../../utilities/makeGetSortingRule';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useGetCompanyFilter from '../../../utilities/useGetCompanyFilter';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useTranslatedString from '../../../utilities/useTranslatedString';
import useListReducer from './useListReducer';

export const getSortField = makeGetSortingRule((field): WebPageSortingField => {
    switch (field) {
        case 'module.company.displayName':
            return WebPageSortingField.Company;
        case 'versioning.createdAt':
            return WebPageSortingField.Date;
        case 'isActive':
            return WebPageSortingField.IsActive;
        case 'pageTitle':
            return WebPageSortingField.PageTitle;

        default:
            throw new Error('Unknown Sorting Field!');
    }
});
type WebPageDataSource = WebPageListDataFragment & { key: Key };

type PaginatedWebpageTable = PaginatedTableProps<WebPageDataSource>;

const WebpageListPage = () => {
    const { t } = useTranslation(['webpageList']);

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Webpage);

    const [state, dispatch] = useListReducer(currentCache);
    const company = useCompany(true);

    const { filter, page, pageSize, sort } = state;
    const { data, loading } = useListWebPagesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { ...filter, companyIds: company ? [company.id] : filter.companyIds },
        },
    });

    const companyFilterList = useGetCompanyFilter({ collection: CompanyFilterListCollection.WebPages });

    const searchBox = useSearchInDropDown();
    const navigate = useNavigate();

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sort: getSortField(sorter),
                    });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filter: {
                            companyIds: !filters.company ? undefined : (filters.company as string[]),
                            isActive: !filters.isActive ? null : filters.isActive[0],
                            pageTitle: !filters.pageTitle ? null : filters.pageTitle[0],
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );
    const total = data?.list.count || 0;
    const dataSource = useMemo(() => data?.list.items || [], [data?.list.items]);
    const translated = useTranslatedString();

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const onRow = useCallback<PaginatedWebpageTable['onRow']>(
        record => ({
            onClick: () => {
                setCache(state);
                navigate(record.id);
            },
        }),
        [navigate, setCache, state]
    );

    return (
        <PaginatedTableWithContext
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('webpageList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['module', 'company', 'displayName']}
                    filterDropdown={props => showFilterDropDown(props, companyFilterList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.companyIds}
                    sortOrder={sort?.field === WebPageSortingField.Company ? sort.orderValue : undefined}
                    title={t('webpageList:columns.company')}
                    sorter
                />
            )}
            <Table.Column
                key="pageTitle"
                dataIndex="pageTitle"
                filterDropdown={searchBox.render('pageTitle')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.pageTitle ? [filter.pageTitle] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                render={title => translated(title)}
                sortOrder={sort?.field === WebPageSortingField.PageTitle ? sort.orderValue : undefined}
                title={t('webpageList:columns.pageTitle')}
                sorter
            />
            <Table.Column
                key="date"
                dataIndex={['versioning', 'createdAt']}
                render={(value, record: WebPageListDataFragment) =>
                    getDateTimeOffsetFormat(value, t, record.module.company.timeZone)
                }
                sortOrder={sort?.field === WebPageSortingField.Date ? sort.orderValue : undefined}
                title={t('webpageList:columns.date')}
                sorter
            />

            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                render={value => renderBooleanIcon(value, true)}
                sortOrder={sort?.field === WebPageSortingField.IsActive ? sort.orderValue : undefined}
                title={t('webpageList:columns.isActive')}
                width={120}
                sorter
            />
        </PaginatedTableWithContext>
    );
};

export default WebpageListPage;
